import { contactUrl, privacyUrl, termsUrl } from '@frontend/constants';
import { useVisibilityController } from '@frontend/react';
import { Drawer, Icon, Text, XStack, YStack, isMobile, useIsOnMobile, useIsOnTablet } from '@frontend/web-react';
import { applyClass } from '@frontend/web-utils';
import { ContactSupportModal, Outlet, SidebarAnchor, SidebarButton, SidebarNavLink } from '@frontend/web/components';
import { SubscriptionBanner } from '@frontend/web/components/DomainSpecific/Account';
import { useAccessControl, useAnalytics, useAuthentication } from '@frontend/web/hooks';
import { createFileRoute } from '@frontend/web/utils';
import { useMatchRoute } from '@tanstack/react-router';

const Settings = () => {
  const { logout } = useAuthentication();
  const { isAccessControlLoading, isClientSubscribed } = useAccessControl();
  const { track } = useAnalytics();
  const [isContactModalOpen, { close: closeContactModal, open: openContactModal }] = useVisibilityController();
  const [isDrawerOpen, { close: closeDrawer, open: openDrawer }] = useVisibilityController();
  const isOnMobile = useIsOnMobile();
  const isOnTablet = useIsOnTablet();
  const isCollapsed = isOnMobile || isOnTablet;

  const openContactUrl = () => window.open(contactUrl);

  const handleLogout = () => {
    track({ event: 'logout', topic: 'auth' });
    logout();
  };

  const matchRoute = useMatchRoute();

  // Mobile Drawer Content - Always show full text (not collapsed)
  const MobileDrawerContent = () => (
    <YStack className="gap-y-xl">
      <YStack className="gap-xs">
        <Text className="mb-sm" type="captionBold">
          Account Settings
        </Text>
        <SidebarNavLink
          isActive={!!matchRoute({ fuzzy: true, to: '/app/profile' })}
          isCollapsed={false}
          label="Profile"
          leftIconName="IconUser"
          onClick={closeDrawer}
          to="/app/profile"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/notifications' })}
          isCollapsed={false}
          label="Preferences"
          leftIconName="IconBell"
          onClick={closeDrawer}
          to="/app/settings/notifications"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/email-password' })}
          isCollapsed={false}
          label="Email & Password"
          leftIconName="IconMail"
          onClick={closeDrawer}
          to="/app/settings/email-password"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/payments' })}
          isCollapsed={false}
          label="Payments"
          leftIconName="IconCreditCard"
          onClick={closeDrawer}
          to="/app/settings/payments"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/invite-friends' })}
          isCollapsed={false}
          label="Invite Friends"
          leftIconName="IconGift"
          onClick={closeDrawer}
          to="/app/settings/invite-friends"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/manage-household' })}
          isCollapsed={false}
          label="Manage Household"
          leftIconName="IconUsers"
          onClick={closeDrawer}
          to="/app/settings/manage-household"
        />
      </YStack>
      <YStack className="gap-xs">
        <Text className="mb-sm" type="captionBold">
          Level Up
        </Text>
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/calendar' })}
          isCollapsed={false}
          label="Calendar"
          leftIconName="IconCalendarEvent"
          onClick={closeDrawer}
          to="/app/settings/calendar"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/download-app' })}
          isCollapsed={false}
          label="Download App"
          leftIconName="IconDownload"
          onClick={closeDrawer}
          to="/app/settings/download-app"
        />
      </YStack>
      <YStack className="gap-xs">
        <Text className="mb-sm" type="captionBold">
          Talk To Us
        </Text>
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/feedback' })}
          isCollapsed={false}
          label="Feedback"
          leftIconName="IconFlag"
          onClick={closeDrawer}
          to="/app/settings/feedback"
        />
        <SidebarAnchor
          href="https://www.getduckbill.com/faq"
          isCollapsed={false}
          label="FAQ"
          leftIconName="IconInfoCircle"
          target="_blank"
        />
        <SidebarButton isCollapsed={false} label="Email Duckbill" leftIconName="IconMail" onClick={openContactUrl} />
      </YStack>
      <YStack className="gap-xs">
        <Text className="mb-sm" type="captionBold">
          The Nitty Gritty
        </Text>
        <SidebarAnchor
          href={termsUrl}
          isCollapsed={false}
          label="Terms of Use"
          leftIconName="IconCircleArrowUpRight"
          target="_blank"
        />
        <SidebarAnchor
          href={privacyUrl}
          isCollapsed={false}
          label="Privacy Policy"
          leftIconName="IconCircleArrowUpRight"
          target="_blank"
        />
      </YStack>
      <YStack className="gap-xs">
        <SidebarButton
          color="danger"
          isCollapsed={false}
          label="Logout"
          leftIconName="IconLogout"
          onClick={handleLogout}
        />
      </YStack>
    </YStack>
  );

  // Desktop Sidebar Content - Uses isCollapsed for responsive behavior
  const DesktopSidebarContent = () => (
    <YStack className="gap-y-xl">
      <YStack className="gap-xs">
        {isCollapsed ? null : (
          <Text className="mb-sm" type="captionBold">
            Account Settings
          </Text>
        )}
        <SidebarNavLink
          isActive={!!matchRoute({ fuzzy: true, to: '/app/profile' })}
          isCollapsed={isCollapsed}
          label="Profile"
          leftIconName="IconUser"
          to="/app/profile"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/notifications' })}
          isCollapsed={isCollapsed}
          label="Preferences"
          leftIconName="IconBell"
          to="/app/settings/notifications"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/email-password' })}
          isCollapsed={isCollapsed}
          label="Email & Password"
          leftIconName="IconMail"
          to="/app/settings/email-password"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/payments' })}
          isCollapsed={isCollapsed}
          label="Payments"
          leftIconName="IconCreditCard"
          to="/app/settings/payments"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/invite-friends' })}
          isCollapsed={isCollapsed}
          label="Invite Friends"
          leftIconName="IconGift"
          to="/app/settings/invite-friends"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/manage-household' })}
          isCollapsed={isCollapsed}
          label="Manage Household"
          leftIconName="IconUsers"
          to="/app/settings/manage-household"
        />
      </YStack>
      <YStack className="gap-xs">
        {isCollapsed ? null : (
          <Text className="mb-sm" type="captionBold">
            Level Up
          </Text>
        )}
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/calendar' })}
          isCollapsed={isCollapsed}
          label="Calendar"
          leftIconName="IconCalendarEvent"
          to="/app/settings/calendar"
        />
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/download-app' })}
          isCollapsed={isCollapsed}
          label="Download App"
          leftIconName="IconDownload"
          to="/app/settings/download-app"
        />
      </YStack>
      <YStack className="gap-xs">
        {isCollapsed ? null : (
          <Text className="mb-sm" type="captionBold">
            Talk To Us
          </Text>
        )}
        <SidebarNavLink
          isActive={!!matchRoute({ to: '/app/settings/feedback' })}
          isCollapsed={isCollapsed}
          label="Feedback"
          leftIconName="IconFlag"
          to="/app/settings/feedback"
        />
        <SidebarAnchor
          className={applyClass(isCollapsed, 'justify-center')}
          href="https://www.getduckbill.com/faq"
          isCollapsed={isCollapsed}
          label="FAQ"
          leftIconName="IconInfoCircle"
          target="_blank"
        />
        {/* On web, sometimes the mail client won't open automatically, so we should tell them where to manually send an email to */}
        <SidebarButton
          className={applyClass(isCollapsed, 'justify-center')}
          isCollapsed={isCollapsed}
          label="Email Duckbill"
          leftIconName="IconMail"
          onClick={isMobile ? openContactUrl : openContactModal}
        />
      </YStack>
      <YStack className="gap-xs">
        {isCollapsed ? null : (
          <Text className="mb-sm" type="captionBold">
            The Nitty Gritty
          </Text>
        )}
        <SidebarAnchor
          className={applyClass(isCollapsed, 'justify-center')}
          href={termsUrl}
          isCollapsed={isCollapsed}
          label="Terms of Use"
          leftIconName="IconCircleArrowUpRight"
          target="_blank"
        />
        <SidebarAnchor
          className={applyClass(isCollapsed, 'justify-center')}
          href={privacyUrl}
          isCollapsed={isCollapsed}
          label="Privacy Policy"
          leftIconName="IconCircleArrowUpRight"
          target="_blank"
        />
      </YStack>
      <YStack className="gap-xs">
        <SidebarButton
          className={applyClass(isCollapsed, 'justify-center')}
          color="danger"
          isCollapsed={isCollapsed}
          label="Logout"
          leftIconName="IconLogout"
          onClick={handleLogout}
        />
      </YStack>
    </YStack>
  );

  return (
    <>
      {isMobile ? (
        <YStack className="relative flex size-full flex-col">
          {/* Hamburger Menu Button - Fixed Header */}
          <XStack className="fixed  z-50 border-b-1 border-b-borderPrimary  p-md bg-cream-100 w-full">
            <Icon name="IconMenu2" onClick={openDrawer} radius="lg" size="lg" />
          </XStack>

          {/* Settings Drawer for Mobile */}
          <Drawer isOpen={isDrawerOpen} onClose={closeDrawer} position="left" size="300px">
            <MobileDrawerContent />
          </Drawer>

          {/* Content with top padding for fixed header and bottom padding for nav */}
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <YStack className="flex-1 overflow-y-auto pb-[80px] pt-[60px]">
            {!isClientSubscribed && !isAccessControlLoading ? (
              <YStack className="p-md">
                <SubscriptionBanner />
              </YStack>
            ) : null}
            <Outlet />
          </YStack>
        </YStack>
      ) : (
        <XStack className="flex-1">
          {/* Sidebar for Desktop */}
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <YStack className="gap-y-xs px-lg py-xxl border-r-1 border-r-borderPrimary lg:min-w-[250px]">
            <DesktopSidebarContent />
          </YStack>

          {/* Content */}
          <YStack className="w-full">
            {!isClientSubscribed && !isAccessControlLoading ? (
              <YStack className="p-md">
                <SubscriptionBanner />
              </YStack>
            ) : null}
            <Outlet />
          </YStack>
        </XStack>
      )}
      <ContactSupportModal isOpen={isContactModalOpen} onClose={closeContactModal} />
    </>
  );
};

export const Route = createFileRoute('/app/settings')({
  component: Settings,
});
